import { useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { appInnerCardStyle, appMutedTextStyle, renderDialogFooter, renderSurfaceHeader } from "../../../components/general/uiSurfaceStyles";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import { buildMedicationScheduleText, deriveEmarOccurrenceStatus, getMedicationScheduleOccurrences, getTodayMedicationTimingMeta } from "../utils/emarScheduleUtils";
import { buildEmarEntryDetailRows } from "../utils/emarEntryDisplayUtils";
import { getEmarProtocolSummaries, getEmarProtocolSummary } from "../utils/emarProtocolUtils";
import {
  PRN_EFFECTIVENESS_OPTIONS,
  PRN_REASON_OPTIONS,
  PRN_SIDE_EFFECT_OPTIONS,
} from "../utils/emarPrnUtils";
import PrnMedicationHistory from "./PrnMedicationHistory";

const medPassActionChipStyle: React.CSSProperties = {
  border: "1px solid #cbd5e1",
  borderRadius: "999px",
  background: "#fff",
  color: "#0f172a",
  fontSize: "0.78rem",
  fontWeight: 700,
  padding: "0.35rem 0.65rem",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const protocolAssessmentDialogStyle: React.CSSProperties = {
  width: "min(56rem, 96vw)",
};

const protocolAssessmentFieldStyle: React.CSSProperties = {
  width: "100%",
  minWidth: 0,
  maxWidth: "10rem",
};

const getProtocolAssessmentFieldLabel = (field: any) => {
  const baseLabel = String(field?.label || field?.key || "Value").trim() || "Value";
  const unit = String(field?.unit || "").trim();
  return unit ? `${baseLabel} (${unit})` : baseLabel;
};

const getProtocolAssessmentFieldPlaceholder = () => "Value";

const STATUS_META: Record<string, { label: string; severity: "success" | "danger" | "warning" | "info" | "secondary" }> = {
  given: { label: "Given", severity: "success" },
  family: { label: "Family", severity: "success" },
  refused: { label: "Refused", severity: "danger" },
  held: { label: "Held", severity: "warning" },
  not_given: { label: "Not Given", severity: "warning" },
  missed: { label: "Missed", severity: "danger" },
  hospitalized: { label: "Hospitalized", severity: "warning" },
  prn_given: { label: "Given", severity: "info" },
};

const isPrnFollowUpPending = (entryLike: any) => {
  const status = String(entryLike?.entryStatus || entryLike?.status || "").trim().toLowerCase();
  if (status !== "prn_given") {
    return false;
  }

  const entry = entryLike?.entry || entryLike;
  return !(
    String(entry?.prnReassessedAt || "").trim()
    && String(entry?.prnEffectiveness || "").trim()
    && String(entry?.prnSideEffectCode || "").trim()
  );
};

const isMedicationExpired = (value: any) => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) {
    return false;
  }
  return normalizedValue < new Date().toISOString().slice(0, 10);
};

const getExpiredMedicationChartingMessage = (medication: any) => {
  const effectiveEndDate = String(medication?.effectiveEndDate || "").trim();
  if (!effectiveEndDate || !isMedicationExpired(effectiveEndDate)) {
    return "";
  }
  return `Charting is blocked because this medication expired on ${effectiveEndDate}. Update the medication before saving another med pass.`;
};

const getIsoDateOnly = (value: Date) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const LOS_ANGELES_DATE_ONLY_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Los_Angeles",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getLosAngelesDateOnly = (value: Date = new Date()) => LOS_ANGELES_DATE_ONLY_FORMATTER.format(value);

const getLocalTimeValueFromIso = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const getLocalTimeValueWithSecondsFromIso = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  const hours = String(parsedDate.getHours()).padStart(2, "0");
  const minutes = String(parsedDate.getMinutes()).padStart(2, "0");
  const seconds = String(parsedDate.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const updateIsoDateTimeParts = (sourceValue: any, nextDate?: string, nextTime?: string) => {
  const baseDate = String(sourceValue || "").trim() ? new Date(String(sourceValue || "").trim()) : new Date();
  if (Number.isNaN(baseDate.getTime())) {
    return "";
  }
  const dateValue = String(nextDate || getIsoDateOnly(baseDate)).trim();
  const timeValue = String(nextTime || getLocalTimeValueFromIso(baseDate)).trim() || "00:00";
  const [year, month, day] = dateValue.split("-").map((part) => Number(part));
  const [hours, minutes, seconds] = timeValue.split(":").map((part) => Number(part));
  if (!year || !month || !day) {
    return "";
  }
  const nextRecordedAt = new Date(year, month - 1, day, Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, Number.isFinite(seconds) ? seconds : 0, 0);
  return Number.isNaN(nextRecordedAt.getTime()) ? "" : nextRecordedAt.toISOString();
};

const formatMedPassTimeChoiceLabel = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return "";
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }
  return parsedDate.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const isFutureChartTimestamp = (value: any) => {
  const timestamp = String(value || "").trim();
  if (!timestamp) {
    return false;
  }
  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }
  return parsedDate.getTime() > Date.now();
};

const getMinutesDifferenceBetweenTimestamps = (firstValue: any, secondValue: any) => {
  const firstDate = new Date(String(firstValue || "").trim());
  const secondDate = new Date(String(secondValue || "").trim());
  if (Number.isNaN(firstDate.getTime()) || Number.isNaN(secondDate.getTime())) {
    return null;
  }
  return Math.round((firstDate.getTime() - secondDate.getTime()) / 60000);
};

const SCHEDULE_TIME_OPTIONS = Array.from({ length: 24 * 4 }, (_, index) => {
  const hours = Math.floor(index / 4);
  const minutes = (index % 4) * 15;
  const value = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  const label = date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
  return { label, value };
});

const getAvailableChartTimeOptions = (entryDate?: string) => {
  const normalizedEntryDate = String(entryDate || "").trim();
  if (!normalizedEntryDate) {
    return SCHEDULE_TIME_OPTIONS;
  }
  const todayDateOnly = getIsoDateOnly(new Date());
  if (normalizedEntryDate !== todayDateOnly) {
    return SCHEDULE_TIME_OPTIONS;
  }
  const currentTimeValue = `${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`;
  return SCHEDULE_TIME_OPTIONS.filter((option) => String(option?.value || "") <= currentTimeValue);
};

const buildGivenTimeSelectionState = ({
  entryDate,
  scheduledClockTime,
  baseTimestamp,
  includePromptReason,
}: {
  entryDate: string,
  scheduledClockTime: string,
  baseTimestamp: string,
  includePromptReason?: boolean,
}) => {
  const currentDateTime = new Date();
  const currentTimeValue = `${String(currentDateTime.getHours()).padStart(2, "0")}:${String(currentDateTime.getMinutes()).padStart(2, "0")}:${String(currentDateTime.getSeconds()).padStart(2, "0")}`;
  const fallbackBaseTimestamp = String(baseTimestamp || "").trim() || currentDateTime.toISOString();
  const currentRecordedAt = updateIsoDateTimeParts(fallbackBaseTimestamp, entryDate, currentTimeValue);
  const scheduledRecordedAt = scheduledClockTime
    ? updateIsoDateTimeParts(fallbackBaseTimestamp, entryDate, scheduledClockTime)
    : currentRecordedAt;
  const scheduledIsFuture = !!scheduledClockTime && isFutureChartTimestamp(scheduledRecordedAt);
  const scheduledOffsetMinutes = scheduledClockTime
    ? getMinutesDifferenceBetweenTimestamps(currentRecordedAt, scheduledRecordedAt)
    : null;
  const shouldShowPrompt = !!includePromptReason && !!scheduledClockTime && scheduledOffsetMinutes !== null && Math.abs(scheduledOffsetMinutes) > 60;
  const timingDirection = scheduledOffsetMinutes !== null && scheduledOffsetMinutes < 0 ? "before" : "after";
  const roundedHours = scheduledOffsetMinutes === null
    ? ""
    : (Math.abs(scheduledOffsetMinutes) / 60).toFixed(1).replace(/\.0$/, "");

  return {
    currentRecordedAt,
    scheduledRecordedAt,
    scheduledOffsetMinutes,
    givenTimeChoice: shouldShowPrompt && !scheduledIsFuture ? "scheduled" : (!scheduledIsFuture && scheduledClockTime ? "scheduled" : "current"),
    showGivenTimeChoiceDialog: shouldShowPrompt && !scheduledIsFuture,
    givenTimePromptReason: shouldShowPrompt
      ? `This med is being charted about ${roundedHours} hour${roundedHours === "1" ? "" : "s"} ${timingDirection} the scheduled time. Choose whether to save it at the scheduled time or the current time.`
      : "",
    givenTimeOptions: [
      {
        value: "scheduled",
        label: "Given at scheduled time",
        disabled: !scheduledClockTime || scheduledIsFuture,
        helper: !scheduledClockTime
          ? "No scheduled clock time set"
          : scheduledIsFuture
            ? `${formatMedPassTimeChoiceLabel(scheduledRecordedAt)} is in the future`
            : formatMedPassTimeChoiceLabel(scheduledRecordedAt),
        recordedAt: scheduledRecordedAt,
      },
      {
        value: "current",
        label: "Given at current time",
        helper: formatMedPassTimeChoiceLabel(currentRecordedAt),
        recordedAt: currentRecordedAt,
      },
      {
        value: "custom",
        label: "Exact time",
        helper: "Choose a specific charting time below.",
        recordedAt: currentRecordedAt,
      },
    ],
  };
};

const getScheduledChartUnlockTime = (scheduledDate?: Date | null) => {
  if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
    return null;
  }
  return new Date(scheduledDate.getTime() - (60 * 60 * 1000));
};

const canChartScheduledOccurrenceNow = (occurrence: any) => {
  const unlockTime = getScheduledChartUnlockTime(occurrence?.timingMeta?.scheduledDate);
  if (!unlockTime) {
    return true;
  }
  return Date.now() >= unlockTime.getTime();
};

const formatChartUnlockTimeLabel = (scheduledDate?: Date | null) => {
  const unlockTime = getScheduledChartUnlockTime(scheduledDate);
  if (!unlockTime) {
    return "";
  }
  return unlockTime.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const buildOccurrenceSummaryTag = (
  occurrence: any,
  statusMeta?: { label: string; severity: string } | null,
) => {
  const timingMeta = occurrence?.timingMeta || {};
  const timeLabel = String(occurrence?.occurrenceLabel || timingMeta?.scheduledLabel || "Scheduled").trim() || "Scheduled";
  const isPrnOccurrence = String(timeLabel).trim().toUpperCase() === "PRN";
  const suffix = timeLabel && timeLabel !== "Scheduled" ? ` ${timeLabel}` : "";

  if (occurrence?.entryStatus === "prn_given" && !String(occurrence?.entry?.prnReassessedAt || "").trim()) {
    return {
      label: `Follow-up Pending${suffix}`,
      severity: "info",
    };
  }
  if (statusMeta) {
    return {
      label: `${statusMeta.label}${suffix}`,
      severity: occurrence?.entryStatus === "prn_given" ? "success" : statusMeta.severity,
    };
  }
  if (timingMeta?.isOverdue) {
    return {
      label: `Missed${suffix}`,
      severity: "danger",
    };
  }
  if (timingMeta?.isUpcoming) {
    return {
      label: `Upcoming${suffix}`,
      severity: "secondary",
    };
  }
  if (isPrnOccurrence) {
    return null;
  }
  return {
    label: `Pending${suffix}`,
    severity: timingMeta?.isDueSoon ? "info" : "secondary",
  };
};

type Props = {
  residentId?: string;
  medications: any[];
  todayEntries: any[];
  savingEntry: boolean;
  savingEntryKey?: string;
  onSaveEntry: (payload: any) => Promise<any>;
  onResolveProtocol?: (payload: any) => Promise<any>;
  readOnly?: boolean;
  requireExceptionNotes?: boolean;
  layoutMode?: "grouped" | "all" | "occurrences";
  compactActions?: boolean;
  useActionDialog?: boolean;
  timeBucket?: "all" | "upcoming" | "past" | "notScheduled";
  passFilter?: "all" | "pending" | "charted";
  emptyMessage?: string;
  todayDate?: string;
};

const ResidentEmarTodayPanel = ({
  medications,
  residentId = "",
  todayEntries,
  savingEntry,
  savingEntryKey = "",
  onSaveEntry,
  onResolveProtocol,
  readOnly = false,
  requireExceptionNotes = true,
  layoutMode = "grouped",
  compactActions = false,
  useActionDialog = false,
  timeBucket = "all",
  passFilter = "all",
  emptyMessage = "No passes match this filter.",
  todayDate = getLosAngelesDateOnly(),
}: Props) => {
  const mobile = useResponsive().isMobile;
  const [showMedPassActionDialog, setShowMedPassActionDialog] = useState(false);
  const [medPassActionDraft, setMedPassActionDraft] = useState("");
  const [prnDoseNote, setPrnDoseNote] = useState("");
  const [prnReasonCode, setPrnReasonCode] = useState("");
  const [prnReassessedAt, setPrnReassessedAt] = useState("");
  const [prnEffectiveness, setPrnEffectiveness] = useState("");
  const [prnSideEffectCode, setPrnSideEffectCode] = useState("");
  const [prnSideEffectNote, setPrnSideEffectNote] = useState("");
  const [prnFollowUpNote, setPrnFollowUpNote] = useState("");
  const [protocolAssessmentValues, setProtocolAssessmentValues] = useState<Record<string, string>>({});
  const [protocolDoseValue, setProtocolDoseValue] = useState("");
  const [protocolDecisionNote, setProtocolDecisionNote] = useState("");
  const [protocolResolution, setProtocolResolution] = useState<any>(null);
  const [protocolAssessmentError, setProtocolAssessmentError] = useState("");
  const [showProtocolConditionsDialog, setShowProtocolConditionsDialog] = useState(false);
  const [resolvingProtocol, setResolvingProtocol] = useState(false);
  const [pendingMedPassAction, setPendingMedPassAction] = useState<null | {
    medicationId: string;
    medicationName: string;
    targetEntryId?: string;
    status: string;
    scheduledTimeKey: string;
    scheduledTime: string;
    scheduledClockTime?: string;
    entryDate?: string;
    recordedAt?: string;
    givenTimeChoice?: string;
    showGivenTimeChoiceDialog?: boolean;
    givenTimePromptReason?: string;
    givenTimeOptions?: Array<{ value: string; label: string; helper?: string; recordedAt?: string; disabled?: boolean }>;
    expectedVersion?: number;
    prnWorkflowStage?: "administer" | "follow_up";
    protocolEnabled?: boolean;
    protocolDefinition?: any;
    protocolDefinitions?: any[];
  }>(null);

  const entryByOccurrenceKey = todayEntries.reduce<Record<string, any>>((entriesByOccurrenceKey, item: any) => {
      const scheduledTimeKey = String(item?.scheduledTimeKey || (String(item?.status || "").toLowerCase() === "prn_given" ? "prn" : "default")).trim().toLowerCase();
      const medicationId = String(item?.medicationId || "").trim();
      if (!medicationId) {
        return entriesByOccurrenceKey;
      }

      const normalizedScheduledTimeKey = scheduledTimeKey.startsWith("prn") ? "prn" : scheduledTimeKey;
      const occurrenceKey = `${medicationId}::${normalizedScheduledTimeKey}`;
      const existingItem = entriesByOccurrenceKey[occurrenceKey];
      if (!existingItem) {
        entriesByOccurrenceKey[occurrenceKey] = item;
        return entriesByOccurrenceKey;
      }

      if (normalizedScheduledTimeKey === "prn") {
        const existingPending = isPrnFollowUpPending(existingItem);
        const nextPending = isPrnFollowUpPending(item);
        if (nextPending && !existingPending) {
          entriesByOccurrenceKey[occurrenceKey] = item;
          return entriesByOccurrenceKey;
        }
        if (nextPending === existingPending) {
          const existingTime = new Date(existingItem?.modifiedAt || existingItem?.recordedAt || existingItem?.createdAt || 0).getTime();
          const nextTime = new Date(item?.modifiedAt || item?.recordedAt || item?.createdAt || 0).getTime();
          if (nextTime >= existingTime) {
            entriesByOccurrenceKey[occurrenceKey] = item;
          }
        }
      }

      return entriesByOccurrenceKey;
    }, {});

  const closeMedPassActionDialog = () => {
    setShowMedPassActionDialog(false);
    setShowProtocolConditionsDialog(false);
    setMedPassActionDraft("");
    setPrnDoseNote("");
    setPrnReasonCode("");
    setPrnReassessedAt("");
    setPrnEffectiveness("");
    setPrnSideEffectCode("");
    setPrnSideEffectNote("");
    setPrnFollowUpNote("");
    setProtocolAssessmentValues({});
    setProtocolDoseValue("");
    setProtocolDecisionNote("");
    setProtocolResolution(null);
    setPendingMedPassAction(null);
  };

  const initializeProtocolAssessmentState = (medicationLike: any, entryLike?: any) => {
    const protocolSummary = getEmarProtocolSummary(medicationLike?.medicationSnapshot || medicationLike || {});
    const assessment = entryLike?.protocolAssessment;
    if (!protocolSummary) {
      setProtocolAssessmentValues({});
      setProtocolDoseValue("");
      setProtocolDecisionNote("");
      setProtocolResolution(null);
      return;
    }
    const values = Object.fromEntries(
      (protocolSummary.parameterSchema || []).map((field: any) => {
        const key = String(field?.key || "").trim();
        return [key, String(assessment?.parameterValues?.[key] || "")];
      }),
    );
    setProtocolAssessmentValues(values);
    setProtocolDoseValue(String(assessment?.doseValue || ""));
    setProtocolDecisionNote(String(assessment?.decisionNote || ""));
    setProtocolResolution(assessment?.resolution || null);
  };

  const resolvePendingProtocol = async () => {
    if (!onResolveProtocol || !pendingMedPassAction?.medicationId) {
      return;
    }
    const protocolSummary = getEmarProtocolSummary({
      protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
      protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
    });
    if (!protocolSummary) {
      return;
    }
    const missingField: any = (protocolSummary.parameterSchema || []).find((field: any) => field?.required && !String(protocolAssessmentValues[String(field?.key || "").trim()] || "").trim());
    if (missingField) {
      setProtocolAssessmentError(`Add ${missingField.label || "the required assessment value"} before showing the given action.`);
      return;
    }
    setProtocolAssessmentError("");
    setResolvingProtocol(true);
    try {
      const response = await onResolveProtocol({
        medicationId: pendingMedPassAction.medicationId,
        entryDate: "",
        scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
        protocolAssessment: {
          parameterValues: protocolAssessmentValues,
        },
      });
      if (!hasProtocolRecommendation(response, pendingMedPassAction?.status, response?.resolvedDose)) {
        setProtocolResolution(null);
        setProtocolDoseValue("");
        setProtocolDecisionNote("");
        setMedPassActionDraft("");
        setProtocolAssessmentError("The entered parameters do not produce a valid recommendation. Reach out to the provider to check the parameters.");
        return;
      }
      setProtocolResolution(response);
      const resolvedDose = String(response?.resolvedDose || "").trim();
      setProtocolDoseValue(["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDose.toLowerCase()) ? "" : resolvedDose);
      setProtocolDecisionNote(String(response?.decisionNote || ""));
      const actionMeta = getProtocolResolvedActionMeta(response, pendingMedPassAction?.status);
      setPendingMedPassAction((current) => current ? {
        ...current,
        status: actionMeta.nextStatus,
      } : current);
    } finally {
      setResolvingProtocol(false);
    }
  };

  const updateProtocolAssessmentFieldValue = (fieldKey: string, nextValue: string) => {
    setProtocolAssessmentValues((current) => ({ ...current, [fieldKey]: nextValue }));
    setProtocolResolution(null);
    setProtocolAssessmentError("");
  };

  const protocolResolutionRequiredForSave = (protocolSummary: any, status: any) => (
    !!protocolSummary && ["given", "prn_given"].includes(String(status || "").toLowerCase())
  );

  const getProtocolConditionDisplayParts = (field: any, condition: any) => {
    const fieldLabel = String(field?.label || field?.key || "Value").trim() || "Value";
    const unit = String(field?.unit || "").trim();
    const equalsValue = String(condition?.equalsValue || "").trim();
    const minValue = String(condition?.minValue || "").trim();
    const maxValue = String(condition?.maxValue || "").trim();
    const valueSuffix = unit ? ` ${unit}` : "";
    if (equalsValue) {
      return `${fieldLabel} = ${equalsValue}${valueSuffix}`;
    }
    if (minValue && maxValue) {
      return `${fieldLabel} ${minValue}-${maxValue}${valueSuffix}`;
    }
    if (minValue) {
      return `${fieldLabel} >= ${minValue}${valueSuffix}`;
    }
    if (maxValue) {
      return `${fieldLabel} <= ${maxValue}${valueSuffix}`;
    }
    return "";
  };

  const renderProtocolDefinitionsOverview = (protocolDefinitions: any[]) => {
    if (!protocolDefinitions.length) {
      return null;
    }
    return (
      <div className="col-12">
        <div style={{ border: "1px solid #dbeafe", background: "#ffffff", borderRadius: "12px", padding: "0.7rem 0.8rem" }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>All conditions</div>
          <div className="mt-2 flex flex-column gap-2">
            {protocolDefinitions.map((definition: any, definitionIndex: number) => (
              <div key={`${String(definition?.category || "protocol")}-${definitionIndex}`} style={{ borderTop: definitionIndex === 0 ? "none" : "1px solid #e2e8f0", paddingTop: definitionIndex === 0 ? 0 : "0.6rem" }}>
                <div className="flex align-items-center gap-2 flex-wrap">
                  <Tag value={String(definition?.label || "Protocol")} severity="info" rounded />
                  {String(definition?.guidanceAction || "").trim() ? (
                    <Tag value={String(definition.guidanceAction).replaceAll("_", " ")} severity="secondary" rounded />
                  ) : null}
                </div>
                {String(definition?.instructions || "").trim() ? (
                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                    <strong style={{ color: "#334155" }}>Setup:</strong> {definition.instructions}
                  </div>
                ) : null}
                {(Array.isArray(definition?.rules) ? definition.rules : []).length ? (
                  <div className="mt-2 flex flex-column gap-1">
                    {(definition.rules || []).map((rule: any, ruleIndex: number) => {
                      const conditionText = (Array.isArray(definition?.parameterSchema) ? definition.parameterSchema : [])
                        .map((field: any) => getProtocolConditionDisplayParts(field, rule?.conditions?.[field?.key] || {}))
                        .filter(Boolean)
                        .join(" AND ");
                      return (
                        <div key={`${String(rule?.ruleId || "rule")}-${ruleIndex}`} style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                          <strong style={{ color: "#334155" }}>
                            {String(rule?.action || "").trim() ? String(rule.action).replaceAll("_", " ") : `Rule ${ruleIndex + 1}`}
                          </strong>
                          {conditionText ? `: Match all of ${conditionText}` : ""}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const getProtocolResolvedActionMeta = (resolution: any, currentStatus?: string) => {
    const currentStatusKey = String(currentStatus || "").trim().toLowerCase();
    const recommendedAction = String(resolution?.recommendedAction || "").trim().toLowerCase();
    const resolvedDoseValue = String(resolution?.resolvedDose || "").trim();
    const resolvedDoseAction = resolvedDoseValue.toLowerCase();
    const resolvedDoseIsAction = ["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDoseAction);
    const effectiveRecommendedAction = recommendedAction || (resolvedDoseIsAction ? resolvedDoseAction : "");
    const hasMatchedRecommendation = !!effectiveRecommendedAction
      && (resolution?.matched !== false || resolvedDoseIsAction);
    const isHoldAction = ["hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(effectiveRecommendedAction);
    const actionVerb = isHoldAction ? "Hold" : "Give";
    const matchedRuleLabel = String(resolution?.matchedRuleLabel || "").trim();
    const decisionReason = String(resolution?.decisionNote || "").trim();
    const suggestedDose = resolvedDoseIsAction ? "" : resolvedDoseValue;
    const getStatusButtonLabel = (status: string) => ({
      given: "Mark as Given",
      family: "Mark as Given by Family",
      held: "Mark as Held",
      refused: "Mark as Refused",
      not_given: "Mark as Not Given",
      missed: "Mark as Missed",
      hospitalized: "Mark as Hospitalized",
      prn_given: "Mark as Given",
    } as Record<string, string>)[String(status || "").trim().toLowerCase()] || "Save Action";
    if (!hasMatchedRecommendation) {
      const nextStatus = currentStatusKey === "held"
        ? "held"
        : currentStatusKey === "prn_given"
          ? "prn_given"
          : currentStatusKey === "family"
            ? "family"
            : "given";
      return {
        nextStatus,
        note: "No rule matched. Contact provider.",
        buttonLabel: getStatusButtonLabel(nextStatus),
        actionLabel: "No rule matched - Contact provider",
        dosageLabel: "",
      };
    }
    const explanatoryNote = matchedRuleLabel
      ? `${actionVerb} because the reading matched ${matchedRuleLabel}.${decisionReason ? ` ${decisionReason}` : ""}`
      : decisionReason
        ? `${actionVerb} because ${decisionReason.charAt(0).toLowerCase()}${decisionReason.slice(1)}`
        : actionVerb;
    const noteWithDose = suggestedDose && !isHoldAction
      ? `${explanatoryNote} Suggested dose: ${suggestedDose}.`
      : explanatoryNote;
    if (isHoldAction) {
      return {
        nextStatus: "held",
        note: explanatoryNote,
        buttonLabel: getStatusButtonLabel("held"),
        actionLabel: actionVerb,
        dosageLabel: suggestedDose,
      };
    }
    if (currentStatusKey === "prn_given") {
      return {
        nextStatus: "prn_given",
        note: noteWithDose,
        buttonLabel: getStatusButtonLabel("prn_given"),
        actionLabel: actionVerb,
        dosageLabel: suggestedDose,
      };
    }
    return {
      nextStatus: currentStatusKey === "family" ? "family" : "given",
      note: noteWithDose,
      buttonLabel: getStatusButtonLabel(currentStatusKey === "family" ? "family" : "given"),
      actionLabel: actionVerb,
      dosageLabel: suggestedDose,
    };
  };

  const hasProtocolRecommendation = (resolution: any, currentStatus?: string, doseValue = "") => {
    if (!resolution) {
      return false;
    }
    const resolvedDose = String(resolution?.resolvedDose || doseValue || "").trim().toLowerCase();
    const hasActionLikeDose = ["give", "hold", "do_not_give", "alert", "call_provider", "call_911", "custom_action"].includes(resolvedDose);
    if (resolution?.matched === false && !hasActionLikeDose) return false;
    const actionMeta = getProtocolResolvedActionMeta(resolution, currentStatus);
    return Boolean(
      String(actionMeta?.dosageLabel || doseValue || "").trim()
      || String(resolution?.decisionNote || "").trim()
      || String(actionMeta?.actionLabel || "").trim(),
    );
  };

  const shouldShowProtocolNoteInput = () => {
    const protocolSummary = getEmarProtocolSummary({
      protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
      protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
    });
    const isProtocolChartingAction = !!protocolSummary
      && ["given", "family"].includes(String(pendingMedPassAction?.status || "").toLowerCase());
    if (!isProtocolChartingAction) {
      return true;
    }
    return true;
  };

  const shouldShowProtocolAction = (resolution: any, currentStatus?: string, doseValue = "") => {
    const actionMeta = getProtocolResolvedActionMeta(resolution, currentStatus);
    const actionLabel = String(actionMeta?.actionLabel || "").trim();
    const dosageLabel = String(actionMeta?.dosageLabel || doseValue || "").trim().toLowerCase();
    const decisionNote = String(resolution?.decisionNote || "").trim();
    return Boolean(actionLabel)
      && (actionLabel.toLowerCase() !== "give" || (!dosageLabel && !decisionNote))
      && !(actionLabel.toLowerCase() === "give" && /\b(do not give|don't give|hold)\b/.test(dosageLabel));
  };

  const openMedPassActionDialog = (
    row: any,
    status: "given" | "family" | "refused" | "held" | "not_given" | "missed" | "hospitalized" | "prn_given",
  ) => {
    if (["given", "refused", "held", "not_given"].includes(status) && !canChartScheduledOccurrenceNow(row)) {
      return;
    }
    const entryDate = todayDate;
    const scheduledClockTime = String(row?.timingMeta?.scheduledClockTime || row?.medication?.scheduledClockTime || "").trim();
    const givenTimeState = ["given", "family"].includes(status)
      ? buildGivenTimeSelectionState({
        entryDate,
        scheduledClockTime,
        baseTimestamp: new Date().toISOString(),
        includePromptReason: true,
      })
      : null;
    if (givenTimeState && !givenTimeState.showGivenTimeChoiceDialog && row?.medication?.protocolEnabled !== true) {
      void onSaveEntry({
        medicationId: row?.medicationId,
        status,
        scheduledTimeKey: row?.scheduledTimeKey || "default",
        note: String(row?.entry?.note || "").trim(),
        scheduledTime: row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
        entryDate,
        recordedAt: givenTimeState.currentRecordedAt,
        expectedVersion: Number(row?.entry?.version || 0),
      });
      return;
    }
    const isNewPrnAction = row?.medication?.prn === true;
    const existingEntryForDialog = isNewPrnAction ? null : row?.entry;
    setPendingMedPassAction({
      medicationId: row?.medicationId,
      medicationName: row?.medication?.medicationName || row?.medicationName || "Medication",
      protocolEnabled: row?.medication?.protocolEnabled === true,
      protocolDefinition: row?.medication?.protocolDefinition || row?.entry?.medicationSnapshot?.protocolDefinition || null,
      protocolDefinitions: Array.isArray(row?.medication?.protocolDefinitions) ? row.medication.protocolDefinitions : Array.isArray(row?.entry?.medicationSnapshot?.protocolDefinitions) ? row.entry.medicationSnapshot.protocolDefinitions : [],
      targetEntryId: isNewPrnAction ? "" : (row?.entry?.entryId || ""),
      status,
      scheduledTimeKey: row?.scheduledTimeKey || "default",
      scheduledTime: row?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
      scheduledClockTime,
      entryDate,
      recordedAt: givenTimeState
        ? (givenTimeState.givenTimeChoice === "scheduled" ? givenTimeState.scheduledRecordedAt : givenTimeState.currentRecordedAt)
        : status === "prn_given" ? new Date().toISOString() : "",
      givenTimeChoice: givenTimeState?.givenTimeChoice,
      showGivenTimeChoiceDialog: givenTimeState?.showGivenTimeChoiceDialog,
      givenTimePromptReason: givenTimeState?.givenTimePromptReason,
      givenTimeOptions: givenTimeState?.givenTimeOptions,
      expectedVersion: isNewPrnAction ? 0 : Number(row?.entry?.version || 0),
      prnWorkflowStage: "administer",
    });
    setMedPassActionDraft(String(existingEntryForDialog?.note || ""));
    setPrnDoseNote(String(existingEntryForDialog?.note || ""));
    setPrnReasonCode(String(existingEntryForDialog?.prnReasonCode || ""));
    setPrnReassessedAt(String(existingEntryForDialog?.prnReassessedAt || ""));
    setPrnEffectiveness(String(existingEntryForDialog?.prnEffectiveness || ""));
    setPrnSideEffectCode(String(existingEntryForDialog?.prnSideEffectCode || ""));
    setPrnSideEffectNote(String(existingEntryForDialog?.prnSideEffectNote || ""));
    setPrnFollowUpNote(String(existingEntryForDialog?.prnFollowUpNote || ""));
    initializeProtocolAssessmentState(row?.medication || existingEntryForDialog?.medicationSnapshot, existingEntryForDialog);
    setShowMedPassActionDialog(true);
  };

  const getRowOccurrenceKey = (row: any) => (
    `${String(row?.medicationId || "").trim()}::${String(row?.scheduledTimeKey || "default").trim().toLowerCase()}`
  );

  const saveMedPassActionDialog = async () => {
    if (!pendingMedPassAction?.medicationId || !pendingMedPassAction?.status) {
      return;
    }

    const protocolSummary = getEmarProtocolSummary({
      protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
      protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction.protocolDefinitions : [],
      protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
    });
    const protocolAssessment = protocolSummary ? {
      parameterValues: protocolAssessmentValues,
      doseValue: protocolDoseValue.trim(),
      decisionNote: protocolDecisionNote.trim(),
      resolution: protocolResolution,
    } : undefined;

    if (protocolSummary && ["given", "prn_given"].includes(String(pendingMedPassAction.status || "").toLowerCase())) {
      const missingField = (protocolSummary.parameterSchema || []).find((field: any) => field?.required && !String(protocolAssessmentValues[String(field?.key || "").trim()] || "").trim());
      if (missingField) {
        return;
      }
      if (!protocolResolution) {
        return;
      }
      if (protocolSummary.requiresDoseInput && pendingMedPassAction.status !== "held" && !protocolDoseValue.trim()) {
        return;
      }
    }

    if (pendingMedPassAction.status === "prn_given") {
      const isPrnFollowUp = pendingMedPassAction?.prnWorkflowStage === "follow_up";
      if (isPrnFollowUp) {
        if (!prnReassessedAt.trim() || !prnEffectiveness || !prnSideEffectCode) {
          return;
        }
        if (prnSideEffectCode === "other" && !prnSideEffectNote.trim()) {
          return;
        }
      } else {
        if (!prnReasonCode) {
          return;
        }
        if (prnReasonCode === "other" && !prnDoseNote.trim()) {
          return;
        }
      }
      await onSaveEntry({
        medicationId: pendingMedPassAction.medicationId,
        targetEntryId: pendingMedPassAction.targetEntryId,
        status: pendingMedPassAction.status,
        scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
        note: prnDoseNote.trim(),
        scheduledTime: pendingMedPassAction.scheduledTime || "",
        expectedVersion: pendingMedPassAction.expectedVersion || 0,
        prnReasonCode,
        prnReasonLabel: PRN_REASON_OPTIONS.find((item) => item.value === prnReasonCode)?.label || "",
        prnReassessedAt: prnReassessedAt.trim(),
        prnEffectiveness,
        prnEffectivenessLabel: PRN_EFFECTIVENESS_OPTIONS.find((item) => item.value === prnEffectiveness)?.label || "",
        prnSideEffectCode,
        prnSideEffectLabel: PRN_SIDE_EFFECT_OPTIONS.find((item) => item.value === prnSideEffectCode)?.label || "",
        prnSideEffectNote: prnSideEffectNote.trim(),
        prnFollowUpNote: prnFollowUpNote.trim(),
        protocolAssessment,
      });
      closeMedPassActionDialog();
      return;
    }

    if (!["given", "family"].includes(String(pendingMedPassAction.status || "").toLowerCase()) && !medPassActionDraft.trim()) {
      return;
    }

    await onSaveEntry({
      medicationId: pendingMedPassAction.medicationId,
      targetEntryId: pendingMedPassAction.targetEntryId,
      status: pendingMedPassAction.status,
      scheduledTimeKey: pendingMedPassAction.scheduledTimeKey,
      note: medPassActionDraft.trim(),
      scheduledTime: pendingMedPassAction.scheduledTime || "",
      entryDate: pendingMedPassAction.entryDate || "",
      recordedAt: pendingMedPassAction.recordedAt || "",
      expectedVersion: pendingMedPassAction.expectedVersion || 0,
      protocolAssessment,
    });
    closeMedPassActionDialog();
  };

  const renderPrnAdministrationFollowUpFields = () => (
    <>
      <div>
        <label className="block mb-2 font-medium">Time reassessed</label>
        <InputText
          value={prnReassessedAt}
          onChange={(event) => setPrnReassessedAt(event.target.value)}
          placeholder="Example: 3:00 PM"
          style={{ width: "100%" }}
        />
      </div>
      <div>
        <label className="block mb-2 font-medium">Effectiveness</label>
        <Dropdown
          value={prnEffectiveness}
          options={PRN_EFFECTIVENESS_OPTIONS}
          onChange={(event) => setPrnEffectiveness(String(event.value || ""))}
          optionLabel="label"
          optionValue="value"
          placeholder="Select effectiveness"
          style={{ width: "100%" }}
        />
      </div>
      <div>
        <label className="block mb-2 font-medium">Side effects seen</label>
        <Dropdown
          value={prnSideEffectCode}
          options={PRN_SIDE_EFFECT_OPTIONS}
          onChange={(event) => setPrnSideEffectCode(String(event.value || ""))}
          optionLabel="label"
          optionValue="value"
          placeholder="Select side effects"
          style={{ width: "100%" }}
        />
      </div>
      <div>
        <label className="block mb-2 font-medium">Side effect note</label>
        <InputTextarea
          value={prnSideEffectNote}
          onChange={(event) => setPrnSideEffectNote(event.target.value)}
          rows={1}
          placeholder="Add extra side effect context if needed."
          style={{ width: "100%" }}
        />
      </div>
      <div>
        <label className="block mb-2 font-medium">Follow-up note</label>
        <InputTextarea
          value={prnFollowUpNote}
          onChange={(event) => setPrnFollowUpNote(event.target.value)}
          rows={1}
          placeholder="Example: Pain improved after reassessment."
          style={{ width: "100%" }}
        />
      </div>
    </>
  );

  const medicationRows = medications.map((medication: any) => {
    const medicationId = String(medication?.medicationId || "").trim();
    const occurrences = getMedicationScheduleOccurrences(medication).map((occurrence: any) => {
      const entry = entryByOccurrenceKey[`${medicationId}::${String(occurrence?.scheduledTimeKey || "default").toLowerCase()}`];
      const rawEntryStatus = String(entry?.status || "").trim().toLowerCase();
      const timingMeta = getTodayMedicationTimingMeta({
        schedule: occurrence?.scheduleText || medication?.schedule || "",
        scheduledClockTime: occurrence?.scheduledClockTime || "",
        prn: medication?.prn,
      }, !!entry);
      const entryStatus = deriveEmarOccurrenceStatus(rawEntryStatus, timingMeta, medication?.prn === true);

      return {
        medication,
        medicationId,
        entry,
        rawEntryStatus,
        entryStatus,
        timingMeta,
        scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
        occurrenceLabel: occurrence?.scheduledLabel || "",
      };
    });

    const hasAttention = occurrences.some((occurrence: any) => (
      ["refused", "held", "not_given", "missed", "hospitalized"].includes(occurrence.entryStatus) || occurrence.timingMeta.isOverdue
    )) || occurrences.some((occurrence: any) => (
      occurrence.entryStatus === "prn_given" && !String(occurrence?.entry?.prnReassessedAt || "").trim()
    ));
    const hasDueNow = occurrences.some((occurrence: any) => occurrence.timingMeta.isDueSoon);
    const hasUpcoming = occurrences.some((occurrence: any) => occurrence.timingMeta.isUpcoming);
    const allCompleted = occurrences.length > 0 && occurrences.every((occurrence: any) => !!occurrence.entryStatus);

    const bucket = medication?.prn && !occurrences.some((occurrence: any) => !!occurrence.entryStatus)
      ? "prn"
      : hasAttention
        ? "needsAttention"
        : allCompleted
          ? "completed"
          : hasDueNow
            ? "dueNow"
            : hasUpcoming
              ? "upcoming"
              : "pending";

    return {
      medication,
      medicationId,
      occurrences,
      bucket,
    };
  });

  const groupedRows = medicationRows.reduce((acc, row: any) => {
    acc[row.bucket].push(row);
    return acc;
  }, {
    needsAttention: [] as any[],
    dueNow: [] as any[],
    upcoming: [] as any[],
    prn: [] as any[],
    pending: [] as any[],
    completed: [] as any[],
  });

  const statusFilteredMedicationRows = medicationRows
    .map((row: any) => ({
      ...row,
      occurrences: row.occurrences.filter((occurrence: any) => {
        if (passFilter === "all") {
          return true;
        }
        const isCharted = Boolean(occurrence?.entry && String(occurrence?.entryStatus || "").trim());
        return passFilter === "charted" ? isCharted : !isCharted;
      }),
    }))
    .filter((row: any) => row.occurrences.length > 0);

  const occurrenceRows = statusFilteredMedicationRows.flatMap((row: any) => row.occurrences.map((occurrence: any) => ({
    medication: row.medication,
    medicationId: row.medicationId,
    occurrences: [occurrence],
    bucket: row.bucket,
  })));

  const filteredOccurrenceRows = occurrenceRows.filter((row: any) => {
    if (timeBucket === "all") {
      return true;
    }
    const scheduledDate = row?.occurrences?.[0]?.timingMeta?.scheduledDate;
    if (!(scheduledDate instanceof Date) || Number.isNaN(scheduledDate.getTime())) {
      return timeBucket === "notScheduled";
    }
    if (timeBucket === "upcoming") {
      return scheduledDate.getTime() > Date.now();
    }
    if (timeBucket === "past") {
      return scheduledDate.getTime() <= Date.now();
    }
    return false;
  });

  const allRows = layoutMode === "occurrences" ? filteredOccurrenceRows : statusFilteredMedicationRows;

  const medicationBodyTemplate = (row: any) => {
    const medication = row.medication;
    const protocolSummary = getEmarProtocolSummary(medication);

    return (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800, color: "#0f172a" }}>{medication?.medicationName || "Medication"}</div>
        {medication?.dosage ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Dose:</strong> {medication.dosage}
          </div>
        ) : null}
        {medication?.route ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Route:</strong> {medication.route}
          </div>
        ) : null}
        {medication?.effectiveEndDate ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: isMedicationExpired(medication?.effectiveEndDate) ? "#b91c1c" : appMutedTextStyle.color }}>
            <strong style={{ color: isMedicationExpired(medication?.effectiveEndDate) ? "#b91c1c" : "#334155" }}>
              {isMedicationExpired(medication?.effectiveEndDate) ? "Expired:" : "Exp:"}
            </strong>{" "}
            {medication.effectiveEndDate}
          </div>
        ) : null}
        {medication?.indication ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <strong style={{ color: "#334155" }}>Reason/Indication:</strong> {medication.indication}
          </div>
        ) : null}
        {medication?.instructions ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Instructions:</strong> {medication.instructions}
          </div>
        ) : null}
        {medication?.handlingNotes ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
            <strong style={{ color: "#334155" }}>Handling notes:</strong> {medication.handlingNotes}
          </div>
        ) : null}
        {medication?.prn && residentId ? <PrnMedicationHistory residentId={residentId} medicationId={String(medication?.medicationId || "")} /> : null}
        {protocolSummary ? (
          <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
            <span style={{ color: "#334155", fontWeight: 700 }}>{`Protocol: ${protocolSummary.label}`}</span>
          </div>
        ) : null}
      </div>
    );
  };

  const statusBodyTemplate = (row: any) => {
    const medication = row.medication;

    return (
      <div style={{ minWidth: 0 }}>
        <div className="flex flex-column gap-2">
          {row.occurrences.map((occurrence: any) => {
            const entry = occurrence.entry;
            const entryStatus = occurrence.entryStatus;
            const statusMeta = STATUS_META[entryStatus];
            const timingMeta = occurrence.timingMeta;
            const detailRows = buildEmarEntryDetailRows(entry, medication).filter((detail: any) => (
              !["recommended action", "resolved dose"].includes(String(detail?.label || "").trim().toLowerCase())
            ));
            const summaryTag = buildOccurrenceSummaryTag(occurrence, statusMeta);
            const shouldShowMissingEntryMessage = !detailRows.length && !occurrence.rawEntryStatus && timingMeta.isOverdue;

            if (!summaryTag && !detailRows.length && !shouldShowMissingEntryMessage) {
              return null;
            }

            return (
              <div
                key={`${row.medicationId}-${occurrence.scheduledTimeKey}`}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "0.65rem 0.75rem",
                  background: "#fff",
                }}
              >
                <div className="flex gap-2 flex-wrap align-items-center">
                  {summaryTag ? <Tag value={summaryTag.label} severity={summaryTag.severity as any} rounded /> : null}
                </div>
                {detailRows.length ? (
                  <div className="mt-2 flex flex-column gap-1">
                    {detailRows.map((item) => (
                      <div key={`${occurrence.scheduledTimeKey}-${item.label}-${item.value}`} style={{ ...appMutedTextStyle, fontSize: "0.82rem", whiteSpace: "pre-wrap" }}>
                        <strong style={{ color: "#334155" }}>{item.label}:</strong> {item.value}
                      </div>
                    ))}
                  </div>
                ) : null}
                {shouldShowMissingEntryMessage ? (
                  <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.82rem", color: "#b91c1c" }}>
                    No charted entry was saved for this scheduled medication pass.
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const actionsBodyTemplate = (row: any) => {
    if (readOnly) {
      return null;
    }

    const expiredMedicationMessage = getExpiredMedicationChartingMessage(row?.medication);
    const isExpiredMedication = !!expiredMedicationMessage;

    const saveExceptionAction = (occurrence: any, status: string, promptLabel: string) => {
      if (isExpiredMedication) {
        return;
      }
      const actionRow = {
        ...row,
        ...occurrence,
        medication: row.medication,
        medicationId: row.medicationId,
      };
      if (useActionDialog && ["given", "family", "refused", "held", "not_given", "missed", "hospitalized", "prn_given"].includes(status)) {
        openMedPassActionDialog(actionRow, status as "given" | "family" | "refused" | "held" | "not_given" | "missed" | "hospitalized" | "prn_given");
        return;
      }
      const note = requireExceptionNotes ? window.prompt(promptLabel) || "" : "";
      if (requireExceptionNotes && !note.trim()) {
        return;
      }
      void onSaveEntry({
        medicationId: row.medicationId,
        status,
        note,
        scheduledTime: occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || row?.medication?.schedule || row?.medication?.scheduledClockTime || "",
        scheduledTimeKey: occurrence?.scheduledTimeKey || "default",
        expectedVersion: Number(occurrence?.entry?.version || 0),
      });
    };

    if (compactActions) {
      const renderActionChip = (
        label: string,
        severityStyles: React.CSSProperties,
        isRowSaving: boolean,
        onClick: () => void,
        isDisabled = false,
      ) => (
        <button
          key={label}
          type="button"
          disabled={isRowSaving || isDisabled}
          onClick={onClick}
          style={{
            ...medPassActionChipStyle,
            ...severityStyles,
            opacity: (isRowSaving || isDisabled) ? 0.7 : 1,
          }}
        >
          {label}
        </button>
      );

      return (
        <div className="flex flex-column gap-2" style={{ minWidth: mobile ? 0 : "17rem" }}>
          {row.occurrences.map((occurrence: any) => {
            const medication = row.medication;
            const entryStatus = occurrence.entryStatus;
            const isLockedStatus = ["given", "family", "hospitalized"].includes(String(entryStatus || "").toLowerCase());
            const isRowSaving = savingEntry && savingEntryKey === getRowOccurrenceKey(occurrence);
            const scheduledTime = occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || medication?.schedule || medication?.scheduledClockTime || "";
            const scheduledTimeKey = occurrence?.scheduledTimeKey || "default";
            const canChartScheduledNow = medication?.prn ? true : canChartScheduledOccurrenceNow(occurrence);
            const chartingAvailableAtLabel = formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate);
            const showTimedScheduledActions = medication?.prn || canChartScheduledNow;

            return (
              <div
                key={`${row.medicationId}-${scheduledTimeKey}`}
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  padding: "0.6rem 0.65rem",
                  background: "#fff",
                }}
              >
                <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
                  {scheduledTime || "Scheduled"}
                </div>
                {isExpiredMedication ? null : (
                  <div className="flex gap-2 flex-wrap">
                    {isLockedStatus
                      ? renderActionChip(
                        entryStatus === "hospitalized" ? "Hospitalized" : entryStatus === "family" ? "Family" : "Given",
                        entryStatus === "hospitalized"
                          ? { background: "#fef3c7", borderColor: "#d97706", color: "#9a3412" }
                          : entryStatus === "family"
                            ? { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" }
                            : { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" },
                        isRowSaving,
                        () => { },
                        true,
                      )
                      : (
                        <>
                          {showTimedScheduledActions ? renderActionChip(
                            "Given",
                            medication?.prn
                              ? isPrnFollowUpPending(occurrence)
                                ? { background: "#e0f2fe", borderColor: "#0284c7", color: "#0c4a6e" }
                                : entryStatus === "prn_given"
                                  ? { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" }
                                : { background: "#fff", borderColor: "#bae6fd", color: "#0c4a6e" }
                              : entryStatus === "given"
                                ? { background: "#dcfce7", borderColor: "#16a34a", color: "#166534" }
                                : { background: "#fff", borderColor: "#bbf7d0", color: "#166534" },
                            isRowSaving,
                            () => {
                              if (medication?.prn) {
                                saveExceptionAction(occurrence, "prn_given", "Add the PRN reason or note.");
                                return;
                              }
                              if (useActionDialog) {
                                openMedPassActionDialog({
                                  ...row,
                                  ...occurrence,
                                  medication: row.medication,
                                  medicationId: row.medicationId,
                                }, "given");
                                return;
                              }
                              void onSaveEntry({ medicationId: row.medicationId, status: "given", scheduledTime, scheduledTimeKey, expectedVersion: Number(occurrence?.entry?.version || 0) });
                            },
                            isExpiredMedication,
                          ) : null}
                          {!medication?.prn ? renderActionChip(
                            "Family",
                            entryStatus === "family"
                              ? { background: "#dbeafe", borderColor: "#2563eb", color: "#1d4ed8" }
                              : { background: "#fff", borderColor: "#bfdbfe", color: "#1d4ed8" },
                            isRowSaving,
                            () => openMedPassActionDialog({
                              ...row,
                              ...occurrence,
                              medication: row.medication,
                              medicationId: row.medicationId,
                            }, "family"),
                            isExpiredMedication,
                          ) : null}
                          {showTimedScheduledActions ? renderActionChip(
                            "Refused",
                            entryStatus === "refused"
                              ? { background: "#fee2e2", borderColor: "#dc2626", color: "#991b1b" }
                              : { background: "#fff", borderColor: "#fecaca", color: "#991b1b" },
                            isRowSaving,
                            () => saveExceptionAction(occurrence, "refused", "Add a short note for refusal."),
                            isExpiredMedication,
                          ) : null}
                          {showTimedScheduledActions ? renderActionChip(
                            "Not Given",
                            entryStatus === "not_given"
                              ? { background: "#fef3c7", borderColor: "#f59e0b", color: "#92400e" }
                              : { background: "#fff", borderColor: "#fde68a", color: "#92400e" },
                            isRowSaving,
                            () => saveExceptionAction(occurrence, "not_given", "Add a short note for not given."),
                            isExpiredMedication,
                          ) : null}
                          {showTimedScheduledActions ? renderActionChip(
                            "Held",
                            entryStatus === "held"
                              ? { background: "#fef3c7", borderColor: "#f59e0b", color: "#92400e" }
                              : { background: "#fff", borderColor: "#fde68a", color: "#92400e" },
                            isRowSaving,
                            () => saveExceptionAction(occurrence, "held", "Add a short note for hold."),
                            isExpiredMedication,
                          ) : null}
                          {renderActionChip(
                            "Hospitalized",
                            entryStatus === "hospitalized"
                              ? { background: "#fef3c7", borderColor: "#d97706", color: "#9a3412" }
                              : { background: "#fff", borderColor: "#fcd34d", color: "#9a3412" },
                            isRowSaving,
                            () => saveExceptionAction(occurrence, "hospitalized", "Add a short hospitalization note."),
                            isExpiredMedication,
                          )}
                        </>
                      )}
                  </div>
                )}
                {isExpiredMedication ? (
                  <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: "#b91c1c" }}>
                    {expiredMedicationMessage}
                  </div>
                ) : !canChartScheduledNow ? (
                  <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem" }}>
                    Given, Refused, Held, and Not Given unlock 1 hour before the scheduled pass time
                    {chartingAvailableAtLabel ? ` (${chartingAvailableAtLabel}).` : "."}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-column gap-2" style={{ minWidth: mobile ? 0 : "19rem" }}>
        {row.occurrences.map((occurrence: any) => {
          const medication = row.medication;
          const entryStatus = occurrence.entryStatus;
          const isLockedStatus = ["given", "family", "hospitalized"].includes(String(entryStatus || "").toLowerCase());
          const isRowSaving = savingEntry && savingEntryKey === getRowOccurrenceKey(occurrence);
          const scheduledTime = occurrence?.occurrenceLabel || occurrence?.timingMeta?.scheduledLabel || medication?.schedule || medication?.scheduledClockTime || "";
          const scheduledTimeKey = occurrence?.scheduledTimeKey || "default";
          const canChartScheduledNow = medication?.prn ? true : canChartScheduledOccurrenceNow(occurrence);
          const chartingAvailableAtLabel = formatChartUnlockTimeLabel(occurrence?.timingMeta?.scheduledDate);
          const showTimedScheduledActions = medication?.prn || canChartScheduledNow;

          return (
            <div
              key={`${row.medicationId}-${scheduledTimeKey}`}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "0.6rem 0.65rem",
                background: "#fff",
              }}
            >
              <div className="mb-2" style={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569" }}>
                {scheduledTime || "Scheduled"}
              </div>
              {isExpiredMedication ? null : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(6.5rem, 1fr))",
                    gap: "0.45rem",
                  }}
                >
                  {isLockedStatus ? (
                    <Button
                      type="button"
                      label={entryStatus === "hospitalized" ? "Hospitalized" : entryStatus === "family" ? "Family" : "Given"}
                      size="small"
                      severity={entryStatus === "hospitalized" ? "warning" : "success"}
                      rounded
                      loading={isRowSaving}
                      disabled
                    />
                  ) : (
                    <>
                      {showTimedScheduledActions ? (
                        <Button
                          type="button"
                          label="Given"
                          size="small"
                          severity={medication?.prn ? "info" : "success"}
                          rounded
                          outlined={medication?.prn ? entryStatus !== "prn_given" : entryStatus !== "given"}
                          loading={isRowSaving}
                          disabled={isExpiredMedication}
                          onClick={() => {
                            if (medication?.prn) {
                              saveExceptionAction(occurrence, "prn_given", "Add the PRN reason or note.");
                              return;
                            }
                            if (useActionDialog) {
                              openMedPassActionDialog({
                                ...row,
                                ...occurrence,
                                medication: row.medication,
                                medicationId: row.medicationId,
                              }, "given");
                              return;
                            }
                            void onSaveEntry({ medicationId: row.medicationId, status: "given", scheduledTime, scheduledTimeKey, expectedVersion: Number(occurrence?.entry?.version || 0) });
                          }}
                        />
                      ) : null}
                      {!medication?.prn ? (
                        <Button
                          type="button"
                          label="Family"
                          size="small"
                          severity="info"
                          rounded
                          outlined={entryStatus !== "family"}
                          loading={isRowSaving}
                          disabled={isExpiredMedication}
                          onClick={() => {
                            openMedPassActionDialog({
                              ...row,
                              ...occurrence,
                              medication: row.medication,
                              medicationId: row.medicationId,
                            }, "family");
                          }}
                        />
                      ) : null}
                      {showTimedScheduledActions ? (
                        <Button
                          type="button"
                          label="Refused"
                          size="small"
                          severity="danger"
                          rounded
                          outlined={entryStatus !== "refused"}
                          loading={isRowSaving}
                          disabled={isExpiredMedication}
                          onClick={() => {
                            saveExceptionAction(occurrence, "refused", "Add a short note for refusal.");
                          }}
                        />
                      ) : null}
                      {showTimedScheduledActions ? (
                        <Button
                          type="button"
                          label="Not Given"
                          size="small"
                          severity="warning"
                          rounded
                          outlined={entryStatus !== "not_given"}
                          loading={isRowSaving}
                          disabled={isExpiredMedication}
                          onClick={() => {
                            saveExceptionAction(occurrence, "not_given", "Add a short note for not given.");
                          }}
                        />
                      ) : null}
                      {showTimedScheduledActions ? (
                        <Button
                          type="button"
                          label="Held"
                          size="small"
                          severity="warning"
                          rounded
                          outlined={entryStatus !== "held"}
                          loading={isRowSaving}
                          disabled={isExpiredMedication}
                          onClick={() => {
                            saveExceptionAction(occurrence, "held", "Add a short note for hold.");
                          }}
                        />
                      ) : null}
                      <Button
                        type="button"
                        label="Hospitalized"
                        size="small"
                        severity="warning"
                        rounded
                        outlined={entryStatus !== "hospitalized"}
                        loading={isRowSaving}
                        disabled={isExpiredMedication}
                        onClick={() => {
                          saveExceptionAction(occurrence, "hospitalized", "Add a short hospitalization note.");
                        }}
                      />
                    </>
                  )}
                </div>
              )}
              {isExpiredMedication ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem", color: "#b91c1c" }}>
                  {expiredMedicationMessage}
                </div>
              ) : !medication?.prn && !canChartScheduledNow ? (
                <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.78rem" }}>
                  Given, Refused, Held, and Not Given unlock 1 hour before the scheduled pass time
                  {chartingAvailableAtLabel ? ` (${chartingAvailableAtLabel}).` : "."}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSection = (title: string, rows: any[], emptyText?: string) => {
    if (!rows.length && !emptyText) {
      return null;
    }

    return (
      <div className="flex flex-column gap-3">
        <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
          <div style={{ fontWeight: 800, color: "#0f172a" }}>{title}</div>
          <Tag value={`${rows.length}`} severity="secondary" rounded />
        </div>
        {rows.length ? (
          <div style={{ ...appInnerCardStyle, padding: "0.35rem 0.35rem 0.15rem" }}>
            {mobile ? (
              <div className="flex flex-column gap-3">
                {rows.map((row: any) => (
                  <div
                    key={row.medicationId}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: 14,
                      padding: "0.85rem",
                      background: "#fff",
                      display: "grid",
                      gap: "0.85rem",
                    }}
                  >
                    <div>{medicationBodyTemplate(row)}</div>
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4rem" }}>
                        Last Entry
                      </div>
                      {statusBodyTemplate(row)}
                    </div>
                    {!readOnly ? (
                      <div>
                        <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4rem" }}>
                          Actions
                        </div>
                        {actionsBodyTemplate(row)}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <DataTable value={rows} responsiveLayout="scroll" size="small" stripedRows>
                <Column header="Medication" body={medicationBodyTemplate} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
                <Column header="Last Entry" body={statusBodyTemplate} style={{ minWidth: "16rem" }} />
                {!readOnly ? <Column header="Actions" body={actionsBodyTemplate} style={{ minWidth: "20rem" }} /> : null}
              </DataTable>
            )}
          </div>
        ) : (
          <div style={{ ...appMutedTextStyle, fontSize: "0.88rem", padding: "0.1rem 0.1rem 0.2rem" }}>
            {emptyText}
          </div>
        )}
      </div>
    );
  };

  if (layoutMode === "all" || layoutMode === "occurrences") {
    return (
      <>
        <div>
          {allRows.length === 0 ? (
            <div style={{ ...appMutedTextStyle, textAlign: "center", padding: "1rem 0.5rem" }}>
              {emptyMessage}
            </div>
          ) : mobile ? (
            <div className="flex flex-column gap-3">
              {allRows.map((row: any) => (
                <div
                  key={row.medicationId}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 14,
                    padding: "0.85rem",
                    background: "#fff",
                    display: "grid",
                    gap: "0.85rem",
                  }}
                >
                  <div>{medicationBodyTemplate(row)}</div>
                  <div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4rem" }}>
                      Last Entry
                    </div>
                    {statusBodyTemplate(row)}
                  </div>
                  {!readOnly ? (
                    <div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.4rem" }}>
                        Actions
                      </div>
                      {actionsBodyTemplate(row)}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <DataTable value={allRows} responsiveLayout="scroll" size="small" stripedRows>
              <Column header="Medication" body={medicationBodyTemplate} style={{ width: "30%", maxWidth: "30%", minWidth: 0, whiteSpace: "normal", overflowWrap: "anywhere" }} />
              <Column header="Last Entry" body={statusBodyTemplate} style={{ minWidth: "16rem" }} />
              {!readOnly ? <Column header="Actions" body={actionsBodyTemplate} style={{ minWidth: "20rem" }} /> : null}
            </DataTable>
          )}
        </div>
        {useActionDialog ? (
          <Dialog
            visible={showMedPassActionDialog}
            style={protocolAssessmentDialogStyle}
            modal
            onHide={closeMedPassActionDialog}
            header={renderSurfaceHeader(
              pendingMedPassAction?.status === "prn_given"
                ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                  ? "PRN Follow-up"
                  : "Record PRN Dose"
                : "Record Medication Action",
              pendingMedPassAction?.status === "prn_given"
                ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                  ? "Document the reassessment, effectiveness, and any side effects for this PRN medication."
                  : "Add the reason before saving this PRN entry."
                : pendingMedPassAction?.status === "hospitalized"
                  ? "Add a hospitalization note before saving this medication status."
                  : "Add a short note before saving this medication status.",
              pendingMedPassAction?.status === "prn_given" ? "pi pi-bolt" : "pi pi-file-edit",
            )}
            footer={(() => {
              const protocolSummary = getEmarProtocolSummary({
                protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
                protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
                protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
              });
              const requiresProtocolResolution = protocolResolutionRequiredForSave(protocolSummary, pendingMedPassAction?.status);
              const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
              if (requiresProtocolResolution) {
                return (
                    <div className="flex justify-content-end gap-2">
                      <Button label="Close" severity="secondary" outlined onClick={closeMedPassActionDialog} />
                      {actionMeta ? (
                        <Button label={actionMeta.buttonLabel} severity="warning" rounded loading={savingEntry} onClick={saveMedPassActionDialog} />
                      ) : null}
                  </div>
                );
              }
              return renderDialogFooter({
                loading: savingEntry,
                onCancel: closeMedPassActionDialog,
                onSubmit: saveMedPassActionDialog,
                cancelButtonProps: { rounded: true },
                submitLabel: pendingMedPassAction?.status === "prn_given"
                  ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                    ? "Save PRN Follow-up"
                    : "Save PRN"
                  : "Save Action",
              });
            })()}
          >
            <div className="pt-2">
              {pendingMedPassAction?.medicationName ? (
                <div
                  style={{
                    border: "1px solid #e2e8f0",
                    background: "#f8fafc",
                    borderRadius: "16px",
                    padding: "0.85rem 0.95rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>{pendingMedPassAction.medicationName}</div>
                  <div className="mt-1 flex gap-2 flex-wrap">
                    <Tag value={STATUS_META[pendingMedPassAction?.status || ""]?.label || "Action"} severity="info" rounded />
                    {String(pendingMedPassAction?.scheduledTime || "").trim() ? (
                      <Tag value={pendingMedPassAction.scheduledTime} severity="secondary" rounded />
                    ) : null}
                  </div>
                </div>
              ) : null}
              {(() => {
                const protocolMedication = {
                  protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
                  protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
                  protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
                };
                const protocolSummary = getEmarProtocolSummary({
                  ...protocolMedication,
                });
                const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
                if (!protocolSummary
                  || !Array.isArray(protocolSummary.parameterSchema)
                  || protocolSummary.parameterSchema.length === 0
                  || !["given", "prn_given"].includes(String(pendingMedPassAction?.status || "").toLowerCase())) {
                  return null;
                }
                return (
                  <div style={{ border: "1px solid #bfdbfe", background: "#f8fbff", borderRadius: "14px", padding: "0.75rem 0.85rem", marginBottom: "0.75rem" }}>
                    <div style={{ fontWeight: 800, color: "#0f172a" }}>Chart Parameters</div>
                    <div className="grid mt-2" style={{ rowGap: "0.5rem" }}>
                      {(protocolSummary.parameterSchema || []).map((field: any) => {
                        const fieldKey = String(field?.key || "").trim();
                        return (
                          <div className="col-12 md:col-6" key={fieldKey}>
                          <div className="flex flex-column gap-1">
                          <label className="font-medium" style={{ whiteSpace: "nowrap", fontSize: "0.84rem" }}>{getProtocolAssessmentFieldLabel(field)}</label>
                            {field?.inputType === "select" ? (
                              <Dropdown
                                value={protocolAssessmentValues[fieldKey] || ""}
                                options={(Array.isArray(field?.options) ? field.options : []).map((option: any) => ({ label: String(option), value: String(option) }))}
                                onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, String(event.value || ""))}
                                placeholder={getProtocolAssessmentFieldPlaceholder()}
                                style={{ ...protocolAssessmentFieldStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                                className="p-inputtext-sm"
                              />
                            ) : (
                              <InputText
                                value={protocolAssessmentValues[fieldKey] || ""}
                                onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, event.target.value)}
                                inputMode="decimal"
                                placeholder={getProtocolAssessmentFieldPlaceholder()}
                                style={{ ...protocolAssessmentFieldStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                                className="p-inputtext-sm"
                              />
                            )}
                          </div>
                          </div>
                        );
                      })}
                      {!protocolResolution ? (
                        <div className="col-12 flex justify-content-end">
                          <Button
                            type="button"
                            label="Show Recommendation"
                            icon="pi pi-bell"
                            severity="warning"
                            rounded
                            loading={resolvingProtocol}
                            onClick={() => { void resolvePendingProtocol(); }}
                          />
                        </div>
                      ) : null}
                      {protocolAssessmentError ? (
                        <div className="col-12" style={{ color: "#b91c1c", fontSize: "0.82rem", fontWeight: 600 }}>
                          {protocolAssessmentError}
                        </div>
                      ) : null}
                      {hasProtocolRecommendation(protocolResolution, pendingMedPassAction?.status, protocolDoseValue) ? (
                        <div className="col-12">
                          <div>
                            {shouldShowProtocolAction(protocolResolution, pendingMedPassAction?.status, protocolDoseValue) ? (
                              <div className="flex align-items-center gap-2 flex-wrap">
                                <span style={{ ...appMutedTextStyle, fontSize: "0.84rem", fontWeight: 700 }}>Recommended action:</span>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "0.22rem 0.55rem",
                                    borderRadius: "999px",
                                    background: "#dbeafe",
                                    color: "#1d4ed8",
                                    fontSize: "0.9rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.01em",
                                  }}
                                >
                                  {actionMeta?.actionLabel}
                                </span>
                              </div>
                            ) : null}
                            {String(actionMeta?.dosageLabel || protocolDoseValue || "").trim() ? (
                              <div className="mt-2 flex align-items-center gap-2 flex-wrap">
                                <span style={{ ...appMutedTextStyle, fontSize: "0.84rem", fontWeight: 700 }}>Suggested dosage:</span>
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    padding: "0.22rem 0.55rem",
                                    borderRadius: "999px",
                                    background: "#fef3c7",
                                    color: "#92400e",
                                    fontSize: "0.9rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.01em",
                                  }}
                                >
                                  {String(actionMeta?.dosageLabel || protocolDoseValue || "").trim()}
                                </span>
                              </div>
                            ) : null}
                            {protocolResolution?.decisionNote ? (
                              <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                                <strong style={{ color: "#334155" }}>Why:</strong> {protocolResolution.decisionNote}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })()}
              {["given", "family"].includes(String(pendingMedPassAction?.status || "").toLowerCase()) && pendingMedPassAction?.showGivenTimeChoiceDialog ? (
                <div style={{ border: "1px solid #dbeafe", background: "#f8fbff", borderRadius: "16px", padding: "0.85rem 0.95rem", marginBottom: "1rem" }}>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>
                    Choose administration time
                  </div>
                  <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                    Choose whether to save it at the scheduled time, the current time, or an exact charting time.
                  </div>
                  <div className="mt-2 grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", width: "min(100%, 48rem)", margin: "0.5rem auto 0", gap: "0.5rem" }}>
                    {(pendingMedPassAction?.givenTimeOptions || []).map((option) => {
                      const isSelected = String(pendingMedPassAction?.givenTimeChoice || "") === String(option?.value || "");
                      return (
                        <div className="col-12 md:col-6" key={option?.value} style={{ padding: 0 }}>
                        <button
                          type="button"
                          disabled={option?.disabled}
                          onClick={() => setPendingMedPassAction((current) => {
                            if (!current) {
                              return current;
                            }
                            return {
                              ...current,
                              givenTimeChoice: option?.value || "",
                              recordedAt: option?.recordedAt || current.recordedAt || "",
                            };
                          })}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            borderRadius: 12,
                            border: isSelected ? "1px solid #2563eb" : "1px solid #dbeafe",
                            background: isSelected ? "#eff6ff" : "#fff",
                            padding: "0.7rem 0.8rem",
                            cursor: option?.disabled ? "not-allowed" : "pointer",
                            opacity: option?.disabled ? 0.6 : 1,
                          }}
                        >
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{option?.label || ""}</div>
                          {option?.helper ? (
                            <div className="mt-1" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                              {option.helper}
                            </div>
                          ) : null}
                        </button>
                        </div>
                      );
                    })}
                  {pendingMedPassAction?.givenTimeChoice === "custom" ? (
                  <div className="col-12 md:col-6" style={{ padding: 0 }}>
                    <div style={{ minHeight: "5.25rem", border: "1px solid #dbe3ef", borderRadius: "14px", padding: "0.8rem 0.9rem", background: "#fff" }}>
                      <label className="block mb-2 font-medium">Exact charting time</label>
                      <Dropdown
                        value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                        options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                        onChange={(event) => {
                          const nextTimeValue = String(event.value || "");
                          setPendingMedPassAction((current) => {
                            if (!current) {
                              return current;
                            }
                            return {
                              ...current,
                              recordedAt: updateIsoDateTimeParts(current.recordedAt, current.entryDate, nextTimeValue),
                            };
                          });
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select time"
                        disabled={pendingMedPassAction?.givenTimeChoice !== "custom"}
                        style={{ width: "100%" }}
                      />
                    </div>
                  </div>
                  ) : null}
                  </div>
                  <div className="mt-3" style={{ ...appMutedTextStyle, fontSize: "0.82rem" }}>
                    This saves the medication as {String(pendingMedPassAction?.status || "").toLowerCase() === "family" ? "administered by family" : "given"} using the selected administration time.
                  </div>
                </div>
              ) : null}
              {pendingMedPassAction?.status === "prn_given" ? (
                <div className="emar-prn-fix-grid">
                  {pendingMedPassAction?.prnWorkflowStage === "follow_up" ? (
                    <>
                      <div style={{ order: 1 }}>
                        <label className="block mb-2 font-medium">Time reassessed</label>
                        <InputText
                          value={prnReassessedAt}
                          onChange={(event) => setPrnReassessedAt(event.target.value)}
                          placeholder="Example: 3:00 PM"
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ order: 2 }}>
                        <label className="block mb-2 font-medium">Effectiveness</label>
                        <Dropdown
                          value={prnEffectiveness}
                          options={PRN_EFFECTIVENESS_OPTIONS}
                          onChange={(event) => setPrnEffectiveness(String(event.value || ""))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select effectiveness"
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ order: 3 }}>
                        <label className="block mb-2 font-medium">Side effects seen</label>
                        <Dropdown
                          value={prnSideEffectCode}
                          options={PRN_SIDE_EFFECT_OPTIONS}
                          onChange={(event) => setPrnSideEffectCode(String(event.value || ""))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select side effects"
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ order: 5 }}>
                        <label className="block mb-2 font-medium">Follow-up note</label>
                        <InputTextarea
                          value={prnFollowUpNote}
                          onChange={(event) => setPrnFollowUpNote(event.target.value)}
                          rows={1}
                          placeholder="Example: Pain improved after reassessment."
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div style={{ order: 4 }}>
                        <label className="block mb-2 font-medium">Side effect note</label>
                        <InputTextarea
                          value={prnSideEffectNote}
                          onChange={(event) => setPrnSideEffectNote(event.target.value)}
                          rows={1}
                          placeholder="Add extra side effect context if needed."
                          style={{ width: "100%" }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="field">
                        <label className="block mb-2 font-medium">Chart time</label>
                        <Dropdown
                          value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                          options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                          onChange={(event) => {
                            const nextTimeValue = String(event.value || "");
                            setPendingMedPassAction((current) => {
                              if (!current) {
                                return current;
                              }
                              return {
                                ...current,
                                recordedAt: updateIsoDateTimeParts(current.recordedAt, current.entryDate, nextTimeValue),
                              };
                            });
                          }}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select time"
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-medium">Reason for PRN</label>
                        <Dropdown
                          value={prnReasonCode}
                          options={PRN_REASON_OPTIONS}
                          onChange={(event) => setPrnReasonCode(String(event.value || ""))}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Select a reason"
                          style={{ width: "100%" }}
                        />
                      </div>
                      <div>
                        <label className="block mb-2 font-medium">Dose note</label>
                        <InputTextarea
                          value={prnDoseNote}
                          onChange={(event) => setPrnDoseNote(event.target.value)}
                          rows={1}
                          placeholder="Example: Tylenol PRN given for headache at 2 PM."
                          style={{ width: "100%" }}
                        />
                      </div>
                      {renderPrnAdministrationFollowUpFields()}
                    </>
                  )}
                </div>
              ) : shouldShowProtocolNoteInput() ? (
                <InputTextarea
                  value={medPassActionDraft}
                  onChange={(event) => setMedPassActionDraft(event.target.value)}
                  rows={4}
                  placeholder={pendingMedPassAction?.status === "refused"
                    ? "Example: Resident declined after explanation."
                    : pendingMedPassAction?.status === "held"
                      ? "Example: Held per provider instruction."
                      : pendingMedPassAction?.status === "not_given"
                        ? "Example: Medication was not available at the scheduled pass."
                        : pendingMedPassAction?.status === "missed"
                          ? "Example: Resident was away during pass."
                          : "Example: Resident sent to hospital on 06/09/2026 and medication not administered."}
                  style={{ width: "100%" }}
                />
              ) : null}
            </div>
          </Dialog>
        ) : null}
        <Dialog
          visible={showProtocolConditionsDialog}
          style={{ width: "min(52rem, 96vw)" }}
          modal
          appendTo={typeof document !== "undefined" ? document.body : undefined}
          baseZIndex={4000}
          onHide={() => setShowProtocolConditionsDialog(false)}
          header={renderSurfaceHeader(
            "Protocol Conditions",
            "Review all configured parameter conditions and recommendations for this medication.",
            "pi pi-question-circle",
          )}
          footer={(
            <div className="flex justify-content-end">
              <Button label="Close" severity="secondary" outlined onClick={() => setShowProtocolConditionsDialog(false)} />
            </div>
          )}
        >
          {(() => {
            const protocolMedication = {
              protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
              protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
              protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
            };
            const protocolDefinitions = getEmarProtocolSummaries(protocolMedication);
            return renderProtocolDefinitionsOverview(protocolDefinitions);
          })()}
        </Dialog>
      </>
    );
  }

  return (
    <>
      <div className="flex flex-column gap-4">
        {renderSection("Needs Attention", groupedRows.needsAttention, "No refused, held, missed, hospitalized, or overdue medications right now.")}
        {renderSection("Due Now", groupedRows.dueNow, "Nothing is due right now.")}
        {renderSection("Upcoming", groupedRows.upcoming)}
        {renderSection("Pending", groupedRows.pending)}
        {renderSection("PRN / As Needed", groupedRows.prn, "No PRN medications are waiting for as-needed use right now.")}
        {renderSection("Completed Today", groupedRows.completed)}
      </div>
      {useActionDialog ? (
        <Dialog
          visible={showMedPassActionDialog}
          style={protocolAssessmentDialogStyle}
          modal
          onHide={closeMedPassActionDialog}
          header={renderSurfaceHeader(
            pendingMedPassAction?.status === "prn_given"
              ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                ? "PRN Follow-up"
                : "Record PRN Medication"
              : "Record Medication Action",
            pendingMedPassAction?.status === "prn_given"
              ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                ? "Document the reassessment, effectiveness, and any side effects for this PRN medication."
                : "Add the PRN reason before saving this chart entry."
              : pendingMedPassAction?.status === "hospitalized"
                ? "Add a hospitalization note before saving this medication status."
                : "Add a short note before saving this medication status.",
            pendingMedPassAction?.status === "prn_given" ? "pi pi-bolt" : "pi pi-file-edit",
          )}
          footer={(() => {
            const protocolSummary = getEmarProtocolSummary({
              protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
              protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
            });
            const requiresProtocolResolution = protocolResolutionRequiredForSave(protocolSummary, pendingMedPassAction?.status);
            const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
            if (requiresProtocolResolution) {
              return (
                <div className="flex justify-content-end gap-2">
                  <Button label="Close" severity="secondary" outlined onClick={closeMedPassActionDialog} />
                  {actionMeta ? (
                    <Button label={actionMeta.buttonLabel} severity="warning" rounded loading={savingEntry} onClick={saveMedPassActionDialog} />
                  ) : null}
                </div>
              );
            }
            return renderDialogFooter({
              loading: savingEntry,
              onCancel: closeMedPassActionDialog,
              onSubmit: saveMedPassActionDialog,
              submitLabel: pendingMedPassAction?.status === "prn_given"
                ? pendingMedPassAction?.prnWorkflowStage === "follow_up"
                  ? "Save PRN Follow-up"
                  : "Save PRN Entry"
                : "Save Action",
            });
          })()}
        >
          <div className="pt-2">
            {pendingMedPassAction?.medicationName ? (
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  background: "#f8fafc",
                  borderRadius: "16px",
                  padding: "0.85rem 0.95rem",
                  marginBottom: "1rem",
                }}
              >
                <div style={{ fontWeight: 800, color: "#0f172a" }}>{pendingMedPassAction.medicationName}</div>
                <div className="mt-1 flex gap-2 flex-wrap">
                  <Tag value={STATUS_META[pendingMedPassAction?.status || ""]?.label || "Action"} severity="info" rounded />
                  {String(pendingMedPassAction?.scheduledTime || "").trim() ? (
                    <Tag value={pendingMedPassAction.scheduledTime} severity="secondary" rounded />
                  ) : null}
                </div>
              </div>
            ) : null}
            {(() => {
              const protocolMedication = {
                protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
                protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
                protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
              };
              const protocolSummary = getEmarProtocolSummary({
                ...protocolMedication,
              });
              const actionMeta = protocolResolution ? getProtocolResolvedActionMeta(protocolResolution, pendingMedPassAction?.status) : null;
              if (!protocolSummary
                || !Array.isArray(protocolSummary.parameterSchema)
                || protocolSummary.parameterSchema.length === 0
                || !["given", "prn_given"].includes(String(pendingMedPassAction?.status || "").toLowerCase())) {
                return null;
              }
              return (
                <div style={{ border: "1px solid #bfdbfe", background: "#f8fbff", borderRadius: "14px", padding: "0.75rem 0.85rem", marginBottom: "0.75rem" }}>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Chart Parameters</div>
                  <div className="grid mt-2" style={{ rowGap: "0.5rem" }}>
                    {(protocolSummary.parameterSchema || []).map((field: any) => {
                      const fieldKey = String(field?.key || "").trim();
                      return (
                        <div className="col-12 md:col-6" key={fieldKey}>
                        <div className="flex flex-column gap-1">
                        <label className="font-medium" style={{ whiteSpace: "nowrap", fontSize: "0.84rem" }}>{getProtocolAssessmentFieldLabel(field)}</label>
                          {field?.inputType === "select" ? (
                            <Dropdown
                              value={protocolAssessmentValues[fieldKey] || ""}
                              options={(Array.isArray(field?.options) ? field.options : []).map((option: any) => ({ label: String(option), value: String(option) }))}
                              onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, String(event.value || ""))}
                              placeholder={getProtocolAssessmentFieldPlaceholder()}
                            style={{ ...protocolAssessmentFieldStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                              className="p-inputtext-sm"
                            />
                          ) : (
                            <InputText
                              value={protocolAssessmentValues[fieldKey] || ""}
                              onChange={(event) => updateProtocolAssessmentFieldValue(fieldKey, event.target.value)}
                              inputMode="decimal"
                              placeholder={getProtocolAssessmentFieldPlaceholder()}
                            style={{ ...protocolAssessmentFieldStyle, width: "100%", maxWidth: "none", minWidth: 0 }}
                              className="p-inputtext-sm"
                            />
                          )}
                          </div>
                        </div>
                      );
                    })}
                    {!protocolResolution ? (
                      <div className="col-12 flex justify-content-end">
                        <Button
                          type="button"
                          label="Show Recommendation"
                          icon="pi pi-bell"
                          severity="warning"
                          rounded
                          loading={resolvingProtocol}
                          onClick={() => { void resolvePendingProtocol(); }}
                        />
                      </div>
                    ) : null}
                    {protocolAssessmentError ? (
                      <div className="col-12" style={{ color: "#b91c1c", fontSize: "0.82rem", fontWeight: 600 }}>
                        {protocolAssessmentError}
                      </div>
                    ) : null}
                    {hasProtocolRecommendation(protocolResolution, pendingMedPassAction?.status, protocolDoseValue) ? (
                      <div className="col-12">
                        <div>
                      {shouldShowProtocolAction(protocolResolution, pendingMedPassAction?.status, protocolDoseValue) ? (
                            <div className="flex align-items-center gap-2 flex-wrap">
                              <span style={{ ...appMutedTextStyle, fontSize: "0.84rem", fontWeight: 700 }}>Recommended action:</span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "0.22rem 0.55rem",
                                  borderRadius: "999px",
                                  background: "#dbeafe",
                                  color: "#1d4ed8",
                                  fontSize: "0.9rem",
                                  fontWeight: 800,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {actionMeta?.actionLabel}
                              </span>
                            </div>
                      ) : null}
                          {String(actionMeta?.dosageLabel || protocolDoseValue || "").trim() ? (
                            <div className="mt-2 flex align-items-center gap-2 flex-wrap">
                              <span style={{ ...appMutedTextStyle, fontSize: "0.84rem", fontWeight: 700 }}>Suggested dosage:</span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  padding: "0.22rem 0.55rem",
                                  borderRadius: "999px",
                                  background: "#fef3c7",
                                  color: "#92400e",
                                  fontSize: "0.9rem",
                                  fontWeight: 800,
                                  letterSpacing: "0.01em",
                                }}
                              >
                                {String(actionMeta?.dosageLabel || protocolDoseValue || "").trim()}
                              </span>
                            </div>
                          ) : null}
                          {protocolResolution?.decisionNote ? (
                            <div className="mt-2" style={{ ...appMutedTextStyle, fontSize: "0.8rem" }}>
                              <strong style={{ color: "#334155" }}>Why:</strong> {protocolResolution.decisionNote}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })()}
            {pendingMedPassAction?.status === "prn_given" ? (
                <div className="grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))", gap: "0.75rem", alignItems: "start", whiteSpace: "nowrap" }}>
                {pendingMedPassAction?.prnWorkflowStage === "follow_up" ? (
                  <>
                      <div style={{ order: 1 }}>
                        <label className="block mb-2 font-medium">Time reassessed</label>
                      <InputText
                        value={prnReassessedAt}
                        onChange={(event) => setPrnReassessedAt(event.target.value)}
                        placeholder="Example: 3:00 PM"
                        style={{ width: "100%" }}
                      />
                    </div>
                      <div style={{ order: 2 }}>
                        <label className="block mb-2 font-medium">Effectiveness</label>
                      <Dropdown
                        value={prnEffectiveness}
                        options={PRN_EFFECTIVENESS_OPTIONS}
                        onChange={(event) => setPrnEffectiveness(String(event.value || ""))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select effectiveness"
                        style={{ width: "100%" }}
                      />
                    </div>
                      <div style={{ order: 3 }}>
                        <label className="block mb-2 font-medium">Side effects observed</label>
                      <Dropdown
                        value={prnSideEffectCode}
                        options={PRN_SIDE_EFFECT_OPTIONS}
                        onChange={(event) => setPrnSideEffectCode(String(event.value || ""))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select side effects"
                        style={{ width: "100%" }}
                      />
                    </div>
                      <div style={{ order: 5 }}>
                        <label className="block mb-2 font-medium">Follow-up note</label>
                      <InputTextarea
                        value={prnFollowUpNote}
                        onChange={(event) => setPrnFollowUpNote(event.target.value)}
                        rows={1}
                        placeholder="Example: Pain improved after reassessment."
                        style={{ width: "100%" }}
                      />
                    </div>
                      <div style={{ order: 4 }}>
                        <label className="block mb-2 font-medium">Side effect note</label>
                      <InputTextarea
                        value={prnSideEffectNote}
                        onChange={(event) => setPrnSideEffectNote(event.target.value)}
                        rows={1}
                        placeholder="Add extra side effect context if needed."
                        style={{ width: "100%" }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block mb-2 font-medium">Chart time</label>
                      <Dropdown
                        value={String(getLocalTimeValueWithSecondsFromIso(pendingMedPassAction?.recordedAt) || "").slice(0, 5)}
                        options={getAvailableChartTimeOptions(pendingMedPassAction?.entryDate)}
                        onChange={(event) => {
                          const nextTimeValue = String(event.value || "");
                          setPendingMedPassAction((current: any) => ({
                            ...current,
                            recordedAt: updateIsoDateTimeParts(current?.recordedAt, current?.entryDate, nextTimeValue),
                          }));
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select time"
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <label className="block mb-2 font-medium">Reason for PRN</label>
                      <Dropdown
                        value={prnReasonCode}
                        options={PRN_REASON_OPTIONS}
                        onChange={(event) => setPrnReasonCode(String(event.value || ""))}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select a reason"
                        style={{ width: "100%" }}
                      />
                    </div>
                    <div>
                      <label className="block mb-2 font-medium">Dose note</label>
                      <InputTextarea
                        value={prnDoseNote}
                        onChange={(event) => setPrnDoseNote(event.target.value)}
                        rows={1}
                        placeholder="Example: Tylenol PRN given for headache at 2 PM."
                        style={{ width: "100%" }}
                      />
                    </div>
                    {renderPrnAdministrationFollowUpFields()}
                  </>
                )}
              </div>
            ) : shouldShowProtocolNoteInput() ? (
              <InputTextarea
                value={medPassActionDraft}
                onChange={(event) => setMedPassActionDraft(event.target.value)}
                rows={4}
                placeholder={pendingMedPassAction?.status === "refused"
                  ? "Example: Resident declined after explanation."
                  : pendingMedPassAction?.status === "held"
                    ? "Example: Held per provider instruction."
                    : pendingMedPassAction?.status === "not_given"
                      ? "Example: Medication was not available at the scheduled pass."
                      : pendingMedPassAction?.status === "missed"
                        ? "Example: Resident was away during pass."
                        : "Example: Resident sent to hospital on 06/09/2026 and medication not administered."}
                style={{ width: "100%" }}
              />
            ) : null}
          </div>
        </Dialog>
      ) : null}
      <Dialog
        visible={showProtocolConditionsDialog}
        style={{ width: "min(52rem, 96vw)" }}
        modal
        appendTo={typeof document !== "undefined" ? document.body : undefined}
        baseZIndex={4000}
        onHide={() => setShowProtocolConditionsDialog(false)}
        header={renderSurfaceHeader(
          "Protocol Conditions",
          "Review all configured parameter conditions and recommendations for this medication.",
          "pi pi-question-circle",
        )}
        footer={(
          <div className="flex justify-content-end">
            <Button label="Close" severity="warning" outlined onClick={() => setShowProtocolConditionsDialog(false)} />
          </div>
        )}
      >
        {(() => {
          const protocolMedication = {
            protocolEnabled: pendingMedPassAction?.protocolEnabled === true,
            protocolDefinitions: Array.isArray(pendingMedPassAction?.protocolDefinitions) ? pendingMedPassAction?.protocolDefinitions : [],
            protocolDefinition: pendingMedPassAction?.protocolDefinition || null,
          };
          const protocolDefinitions = getEmarProtocolSummaries(protocolMedication);
          return renderProtocolDefinitionsOverview(protocolDefinitions);
        })()}
      </Dialog>
    </>
  );
};

export default ResidentEmarTodayPanel;
